'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Send, Phone, Mail, MapPin, Loader2 } from 'lucide-react';
import BookingConfirmationScreen from './BookingConfirmationScreen';
import type { BookingQuoteResult } from './BookingConfirmationScreen';
import { calculateBookingQuote, BASE_DJ_PRICE, SERVICE_PRICES } from '@/lib/booking/pricing';

interface InquiryForm {
  name: string;
  email: string;
  phone: string;
  eventType: string;
  eventDate: string;
  eventTime: string;
  eventHours: string;
  venue: string;
  guestCount: string;
  services: string[];
  message: string;
}

const eventTypes = [
  'Wedding — Reception Only',
  'Karaoke Night',
  'Bingo Night',
  'Trivia Night',
  'Birthday Party',
  'Corporate Event',
  'Bar Event / Residency',
  'Graduation Party',
  'Quinceañera',
  'Other Private Event',
];

const addOnServices = [
  'Photographer', 'Videographer', 'Bartender', 'Caterer',
  'Limo / Transport', 'Security', 'Photo Booth', 'Florist',
  'Baker / Cake', 'Decorator', 'Hair & Makeup', 'Live Musician',
];

const hourOptions = [
  '1', '1.5', '2', '2.5', '3', '3.5', '4', '4.5', '5', '5.5',
  '6', '6.5', '7', '7.5', '8', '9', '10',
];

export default function BookingInquirySection() {
  const [submitting, setSubmitting] = useState(false);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [submittedData, setSubmittedData] = useState<InquiryForm | null>(null);
  const [bookingResult, setBookingResult] = useState<BookingQuoteResult | null>(null);

  const { register, handleSubmit, formState: { errors }, reset } = useForm<InquiryForm>();

  const toggleService = (service: string) => {
    setSelectedServices(prev =>
      prev.includes(service) ? prev.filter(s => s !== service) : [...prev, service]
    );
  };

  const quote = calculateBookingQuote(selectedServices);

  const onSubmit = async (data: InquiryForm) => {
    setSubmitting(true);
    const capturedServices = [...selectedServices];

    try {
      const res = await fetch('/api/booking-inquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          phone: data.phone,
          eventType: data.eventType,
          eventDate: data.eventDate,
          eventTime: data.eventTime || null,
          eventHours: data.eventHours ? parseFloat(data.eventHours) : null,
          venue: data.venue,
          guestCount: data.guestCount,
          services: capturedServices,
          message: data.message,
        }),
      });
      const result = await res.json();

      if (!res.ok || !result.success) {
        throw new Error(result.error || 'Failed to submit inquiry');
      }

      setBookingResult({
        eventId: result.eventId,
        leadId: result.leadId,
        contractId: result.contractId,
        depositScheduleId: result.depositScheduleId,
        balanceScheduleId: result.balanceScheduleId,
        balanceDueDate: result.balanceDueDate,
        quote: result.quote,
      });
      setSubmittedData({ ...data, services: capturedServices });
      reset();
      setSelectedServices([]);
      toast.success(
        capturedServices.length > 0
          ? `Inquiry submitted! Vendors for ${capturedServices.join(', ')} have been notified.`
          : 'Inquiry submitted! Christopher will contact you within 24 hours.'
      );
    } catch (err) {
      console.error('Inquiry submission error:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to submit inquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submittedData && bookingResult) {
    return (
      <BookingConfirmationScreen
        data={submittedData}
        booking={bookingResult}
        onNewInquiry={() => {
          setSubmittedData(null);
          setBookingResult(null);
        }}
      />
    );
  }

  return (
    <section id="booking" className="py-24 relative">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-16">
          <p className="text-xs font-mono tracking-[0.4em] text-muted-foreground uppercase mb-3">Get Started</p>
          <h2 className="text-4xl md:text-5xl font-extrabold mb-4 gold-gradient-text">Book Your Event</h2>
          <p className="text-muted-foreground max-w-xl mx-auto">Fill out the form below and receive an automated quote within minutes. Available vendors will be contacted automatically.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Contact info */}
          <div className="flex flex-col gap-6">
            <div className="rounded-2xl neon-border-gold p-6 card-glass">
              <h3 className="text-lg font-bold text-foreground mb-4">Contact DJ ExXtra</h3>
              <div className="flex flex-col gap-4">
                <a href="tel:2196149534" className="flex items-center gap-3 text-sm text-muted-foreground hover:text-primary transition-colors group">
                  <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center group-hover:bg-primary/20">
                    <Phone size={15} className="text-primary" />
                  </div>
                  219-614-9534
                </a>
                <a href="mailto:maximumeffortmdj@gmail.com" className="flex items-center gap-3 text-sm text-muted-foreground hover:text-primary transition-colors group">
                  <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center group-hover:bg-primary/20">
                    <Mail size={15} className="text-primary" />
                  </div>
                  maximumeffortmdj@gmail.com
                </a>
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center">
                    <MapPin size={15} className="text-primary" />
                  </div>
                  Northwest Indiana · Chicagoland
                </div>
              </div>
            </div>
            <div className="rounded-2xl card-glass p-6">
              <h4 className="text-sm font-bold text-foreground mb-2">What happens next?</h4>
              <ol className="text-xs text-muted-foreground space-y-2 list-decimal list-inside">
                <li>See your quote, contract terms &amp; payment dates instantly</li>
                <li>Optional deposit to hold your date</li>
                <li>Create a client account to track bookings &amp; invoices</li>
              </ol>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="lg:col-span-2 rounded-2xl neon-border p-6 md:p-8 card-glass space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Full Name *</label>
                <input
                  {...register('name', { required: 'Name is required' })}
                  className="w-full px-3 py-2.5 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none focus:border-primary/50"
                  placeholder="Your name"
                />
                {errors.name && <p className="text-xs text-red-400 mt-1">{errors.name.message}</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Email *</label>
                <input
                  type="email"
                  {...register('email', { required: 'Email is required' })}
                  className="w-full px-3 py-2.5 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none focus:border-primary/50"
                  placeholder="you@email.com"
                />
                {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email.message}</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Phone *</label>
                <input
                  {...register('phone', { required: 'Phone is required' })}
                  className="w-full px-3 py-2.5 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none focus:border-primary/50"
                  placeholder="219-555-0100"
                />
                {errors.phone && <p className="text-xs text-red-400 mt-1">{errors.phone.message}</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Event Type *</label>
                <select
                  {...register('eventType', { required: 'Event type is required' })}
                  className="w-full px-3 py-2.5 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none focus:border-primary/50"
                  defaultValue=""
                >
                  <option value="" disabled>Select type</option>
                  {eventTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                {errors.eventType && <p className="text-xs text-red-400 mt-1">{errors.eventType.message}</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Event Date *</label>
                <input
                  type="date"
                  {...register('eventDate', { required: 'Event date is required' })}
                  className="w-full px-3 py-2.5 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none focus:border-primary/50"
                />
                {errors.eventDate && <p className="text-xs text-red-400 mt-1">{errors.eventDate.message}</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Event Start Time</label>
                <input
                  type="time"
                  {...register('eventTime')}
                  className="w-full px-3 py-2.5 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none focus:border-primary/50"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Number of Hours</label>
                <select
                  {...register('eventHours')}
                  className="w-full px-3 py-2.5 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none focus:border-primary/50"
                  defaultValue=""
                >
                  <option value="">Select hours</option>
                  {hourOptions.map(h => (
                    <option key={h} value={h}>{h} {parseFloat(h) === 1 ? 'hour' : 'hours'}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Guest Count</label>
                <input
                  {...register('guestCount')}
                  className="w-full px-3 py-2.5 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none focus:border-primary/50"
                  placeholder="e.g. 150"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Venue</label>
                <input
                  {...register('venue')}
                  className="w-full px-3 py-2.5 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none focus:border-primary/50"
                  placeholder="Venue name or address"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-2">Add-On Services</label>
              <div className="flex flex-wrap gap-2">
                {addOnServices.map(service => {
                  const active = selectedServices.includes(service);
                  return (
                    <button
                      key={service}
                      type="button"
                      onClick={() => toggleService(service)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                        active
                          ? 'bg-primary/15 border-primary/40 text-primary' :'bg-muted/30 border-border text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {service}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Cost Summary */}
            <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-2">
              <p className="text-xs font-bold text-primary uppercase tracking-widest mb-3">Estimated Cost Breakdown</p>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>DJ / MC Base Rate</span>
                <span className="text-foreground font-medium">${BASE_DJ_PRICE.toLocaleString()}</span>
              </div>
              {selectedServices.map(service => (
                <div key={service} className="flex justify-between text-xs text-muted-foreground">
                  <span>{service}</span>
                  <span className="text-foreground font-medium">${(SERVICE_PRICES[service] || 0).toLocaleString()}</span>
                </div>
              ))}
              {selectedServices.length > 0 && (
                <div className="flex justify-between text-xs text-muted-foreground border-t border-border/40 pt-2">
                  <span>Coordination Fee (20%)</span>
                  <span className="text-foreground font-medium">${quote.coordinationFee.toLocaleString()}</span>
                </div>
              )}
              <div className="flex justify-between text-sm font-bold border-t border-primary/30 pt-2 mt-1">
                <span className="text-foreground">Total Estimate</span>
                <span className="text-primary">${quote.totalCost.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Deposit to Hold Date (25%)</span>
                <span className="text-foreground font-medium">${quote.depositAmount.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Balance Due (14 days before event)</span>
                <span className="text-foreground font-medium">${quote.balanceDue.toLocaleString()}</span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Message</label>
              <textarea
                rows={3}
                {...register('message')}
                className="w-full px-3 py-2.5 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none focus:border-primary/50 resize-none"
                placeholder="Tell us about your event..."
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 rounded-xl text-sm font-bold gold-gradient text-black hover:opacity-90 active:scale-[0.99] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting ? (
                <><Loader2 size={16} className="animate-spin" /> Submitting...</>
              ) : (
                <><Send size={16} /> Get My Quote</>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
